import { useState } from 'react';
import { ShieldCheck, Search, Eye, Wrench, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

function Money({ children }) {
  return <span className="font-semibold tabular-nums">{children}</span>;
}

export default function TenantMigrationPage() {
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);
  const [preview, setPreview] = useState({}); // email -> report
  const [applied, setApplied] = useState({}); // email -> report

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.get('/admin/tenant-isolation-audit');
      setAudit(data);
      setPreview({});
      setApplied({});
      if ((data.tenantless_user_count || 0) === 0) {
        toast.success('All clear — no accounts need migration.');
      } else {
        toast.info(`${data.tenantless_user_count} account(s) need attention.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
    } finally {
      setAuditing(false);
    }
  };

  const runPreview = async (email) => {
    setBusyEmail(email);
    try {
      const { data } = await api.post('/admin/tenant-backfill', {
        user_email: email, strategy: 'created_by', dry_run: true,
      });
      setPreview((p) => ({ ...p, [email]: data }));
      toast.info(`Preview ready: ${data.total_records} record(s) would be re-tagged.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setBusyEmail(null);
    }
  };

  const runApply = async (email) => {
    if (!window.confirm(`Re-tag this account's data to its own company?\n\n${email}\n\nThis is safe and only touches records that currently have no company.`)) return;
    setBusyEmail(email);
    try {
      const { data } = await api.post('/admin/tenant-backfill', {
        user_email: email, strategy: 'created_by', dry_run: false, confirm: true,
      });
      setApplied((a) => ({ ...a, [email]: data }));
      toast.success(`Done — ${data.total_records} record(s) re-tagged for ${email}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setBusyEmail(null);
    }
  };

  const users = audit?.tenantless_users || [];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="tenant-migration-page">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Tenant Data Repair</h1>
            <p className="text-sm text-muted-foreground">Re-attach orphaned data to the correct company. Safe — never touches data that already belongs to a company.</p>
          </div>
        </div>

        {/* Step 1 - Check */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Step 1 — Check</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Scans for accounts whose data lost its company tag. Reads only — changes nothing.</p>
            <Button onClick={runAudit} disabled={auditing} data-testid="run-audit-btn">
              {auditing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {auditing ? 'Checking…' : 'Run Check'}
            </Button>

            {audit && (
              <div className="rounded-lg border border-border p-4 bg-muted/30" data-testid="audit-result">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Accounts needing repair</p>
                    <Money>{audit.tenantless_user_count}</Money>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Orphaned records total</p>
                    <Money>{audit.total_orphan_business_records}</Money>
                  </div>
                </div>
                {(audit.total_orphan_business_records || 0) === 0 && (
                  <div className="mt-3 flex items-center gap-2 text-success text-sm">
                    <CheckCircle2 className="w-4 h-4" /> Nothing to migrate. You're all set.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2/3 - per account */}
        {users.length > 0 && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Wrench className="w-4 h-4" /> Step 2 — Repair each account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">For each account: <b>Preview</b> first (changes nothing), then <b>Fix it</b> to apply.</p>
              {users.map((u) => {
                const pv = preview[u.email];
                const ap = applied[u.email];
                const busy = busyEmail === u.email;
                return (
                  <div key={u.id} className="rounded-lg border border-border p-4 space-y-3" data-testid={`migrate-row-${u.email}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold text-foreground">{u.name || '(no name)'}</p>
                        <p className="text-xs text-muted-foreground">{u.email} · {u.role || 'user'}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => runPreview(u.email)} disabled={busy || !!ap} data-testid={`preview-btn-${u.email}`}>
                          {busy && !pv ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />} Preview
                        </Button>
                        <Button size="sm" onClick={() => runApply(u.email)} disabled={busy || !pv || !!ap} data-testid={`apply-btn-${u.email}`}>
                          {busy && pv ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />} Fix it
                        </Button>
                      </div>
                    </div>

                    {pv && !ap && (
                      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm" data-testid={`preview-result-${u.email}`}>
                        <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                          <AlertTriangle className="w-4 h-4" /> Preview — {pv.total_records} record(s) would be re-tagged
                        </div>
                        <ul className="text-xs text-amber-800 grid grid-cols-2 gap-x-4">
                          {(pv.by_collection || []).map((c) => (
                            <li key={c.collection}>{c.collection}: <b>{c.would_migrate ?? c.migrated}</b></li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {ap && (
                      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm" data-testid={`applied-result-${u.email}`}>
                        <div className="flex items-center gap-2 text-emerald-700 font-medium">
                          <CheckCircle2 className="w-4 h-4" /> Done — {ap.total_records} record(s) re-tagged. This account now sees its own data.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
